import { ref, computed, watch, Ref } from 'vue'
import { useTask } from 'vue-concurrency'
import api, { School$Search$Params, School$Search$Response } from '@/api'
import { useCode } from '@/composables'
import { useSearchStore } from '@/stores'

export function useSearchForm() {
  /** キャンペーンコード */
  const { code } = useCode()

  /** 学校検索フォームのストア */
  const search = useSearchStore()

  // 学校検索フォームを監視
  watch(search.form, () => {
    // 学校検索フォームの中身が変更されたらLocalStorageと同期する
    search.store()
  })

  /**
   * 学校検索タスク
   *
   * キャンペーンコードはストアを参照して必ず設定されます。
   * パラメーターを渡した場合はそれを使用します。
   * パラメーターを渡さない場合は保持しているデータを使用します。
   */
  const searchTask = useTask(function* (_, params?: School$Search$Params) {
    const res: School$Search$Response = yield api.school.search({ ...code.query, ...(params ?? search.form.query) })
    return res
  }).drop()

  /**
   * 検索にヒットした学校の一覧
   *
   * 下記のようにデータの構造を変換して扱いやすいようにします。
   *
   * * 変換前
   * `{ 'D100001': {...}, 'T100001': {...}, 'S100001': {...} }`
   *
   * * 変換後
   * `[ {...}, {...}, {...} ]`
   *
   * 読込前は空の配列を返します。
   **/
  const list = computed(() =>
    searchTask.lastSuccessful?.value?.resultList ? Object.values(searchTask.lastSuccessful.value.resultList) : [],
  )

  /** 検索条件 */
  const summary = computed(() => searchTask.lastSuccessful?.value?.resultSummary)

  /**
   * 検索条件があるか否か
   *
   * `resultSummary`に`count`以外のプロパティが含まれているかどうか。
   */
  const hasSummary = computed(() => {
    if (!summary.value) return false
    return Object.keys(summary.value).filter(row => row !== 'count').length > 0
  })

  // 学校検索結果一覧のスクロール表示
  const { listForDisplay, observerTarget } = usePullToFetch<typeof list.value[number]>(list, 10)

  return { search, searchTask, list, summary, hasSummary, listForDisplay, observerTarget }
}

/**
 * スクロール表示用の処理に関する処理だけ隔離したComposable関数
 *
 * 必要な場合は別ファイルへ分けます。
 *
 * @param {T[]} list 全体の一覧
 * @param {number} fetchCount 何件ずつ表示するかの数
 */
function usePullToFetch<T>(list: Ref<T[]>, fetchCount: number) {
  /**
   * 表示用の一覧
   *
   * スクロールとともに順に表示する。
   */
  const listForDisplay = ref<T[]>([]) as Ref<T[]>

  /** 一覧のジェネレーターを作成 */
  const createListGenerator = function* <T>(data: T[]) {
    const rest = [...data]
    while (rest.length > 0) {
      yield rest.splice(0, fetchCount)
    }
  }

  /** 一覧のジェネレーター */
  const listGenerator = ref<Generator<T[], void, unknown> | null>(null)

  /** オブサーバー */
  const listObsever = new IntersectionObserver(
    (entries, observer) => {
      if (entries[0].isIntersecting) {
        const result = listGenerator.value?.next()
        if (result) {
          if (result.done) {
            // 一覧を全て表示したら監視を解除
            observer.disconnect()
          } else {
            // 表示用の一覧に追加する
            listForDisplay.value.push(...result.value)
          }
        }
      }
    },
    { rootMargin: '0px 0px 200px 0px' },
  )

  /**
   * スクロール検知用のHTML要素
   *
   * 監視対象のHTML要素に`ref`属性で紐付けます。
   *
   * e.g.
   * `<div ref="observerTarget">...</div>`
   */
  const observerTarget = ref<HTMLElement | null>(null)

  // 監視対象のHTML要素が読み込まれたら監視開始
  // NOTE: `v-if`だと onMounted が使えないので`watch`で対応
  watch(observerTarget, el => {
    el && listObsever.observe(el)
  })

  // 一覧が取得（再取得）されたら、表示用の一覧を空にして、ジェネレーターを作り（作り直し）ます。
  watch(list, val => {
    listForDisplay.value = []
    listGenerator.value = createListGenerator<T>(val)
  })

  return { listForDisplay, observerTarget }
}
